import React from 'react';
import { useLocation } from 'wouter';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, MapPin, Package, ShieldCheck, User } from 'lucide-react';

interface AdminManualSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment?: {
    id: number;
    taskId: number;
    storeId: number;
    storeName: string;
    storeAddress: string;
    operatorId?: number;
    userId: number;
    operatorName: string;
    campaignId: number;
    campaignName: string;
    taskName: string;
    cycleId: number;
    status: string;
    targetUnits?: number;
    mediaRequirements?: Array<{
      id?: number;
      formatId?: number;
      mediaFormatId?: number;
      formatName?: string;
      name?: string;
      quantity: number;
    }>;
  } | null;
  onSuccess?: () => void;
}

/**
 * Legacy "confirm as admin" modal — retired in favour of Administrative Store Recovery.
 * POST /api/proof-of-posting-v2/admin/submissions/:id returns 410.
 */
export function AdminManualSubmissionModal({
  isOpen,
  onClose,
  assignment,
}: AdminManualSubmissionModalProps) {
  const [, setLocation] = useLocation();

  if (!assignment) return null;

  const assignedOperatorId = assignment.operatorId ?? assignment.userId;
  const recoveryHref =
    assignment.cycleId && assignment.storeId && assignedOperatorId
      ? `/admin/activation-cockpit/recovery/store?cycleId=${assignment.cycleId}&storeId=${assignment.storeId}&assignedOperatorId=${assignedOperatorId}`
      : null;

  const openRecovery = () => {
    if (recoveryHref) {
      setLocation(recoveryHref);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            Complete on behalf of operator
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Direct admin submission on behalf of the operator has been retired. Start an
              on-behalf session instead — it records completion by admin without creating operator
              posting events.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Assignment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                <span className="font-medium">{assignment.operatorName}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span className="font-medium">{assignment.storeName}</span>
              </div>
              <div>{assignment.campaignName}</div>
              <Badge variant="outline">{assignment.taskName || 'Pack task'}</Badge>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Close
            </Button>
            <Button onClick={openRecovery} disabled={!recoveryHref} className="flex-1">
              Complete on behalf
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
